import http from '../../shared/http.js';

// The navbar's bell: the panel of newest tasks, and the badge on it.
//
// The rows are fetched on first open rather than rendered into every page load -
// serving five populated documents on every request to fill a panel most loads
// never open is the wrong trade. That one request also stamps the rows seen, so
// the panel and the badge cannot disagree about which ones were.

const FRAGMENT_HEADER = 'X-Fragment';

// Where the rows go, held at module scope so refreshBadge below can be called
// from another module without looking anything up.
let badge = null;

/**
 * Re-reads the unread count and repaints the badge.
 *
 * Exported because a list action changes the same numbers this shows: closing a
 * task from the dashboard has to move the bell too, or the two parts of the same
 * page contradict each other until the next reload.
 */
export async function refreshBadge() {
  if (!badge) return;

  try {
    const { data } = await http.get('/aufgaben/anzahl');
    const anzahl = data.data.ungelesen;

    badge.textContent = anzahl;
    // Hidden at zero rather than showing a 0: a badge exists to say that
    // something is waiting, and one that is always there stops being read.
    badge.classList.toggle('hidden', anzahl === 0);
  } catch {
    // A bell that cannot count is a bell without a number. The same posture the
    // server-side counter takes - this must never surface as an error toast on
    // a page somebody is using for something else.
  }
}

export function initAufgabenGlocke() {
  const menu = document.getElementById('aufgaben-glocke');
  if (!menu) return;

  badge = document.getElementById('aufgaben-badge');
  const liste = document.getElementById('aufgaben-glocke-liste');
  let geladen = false;

  // `toggle` rather than `click`: <details> fires it for keyboard Enter and
  // Space as well, which is the entire reason this is a <details> at all. A
  // click listener would leave the panel keyboard-openable but never loaded.
  menu.addEventListener('toggle', async () => {
    if (!menu.open || geladen) return;
    geladen = true;

    try {
      const response = await window.fetch('/aufgaben/glocke', {
        headers: { [FRAGMENT_HEADER]: '1' },
        credentials: 'same-origin'
      });

      // A dead session redirects to /login, which arrives here as a page rather
      // than our partial. Follow it instead of pasting a login form into the
      // dropdown.
      if (response.redirected && new URL(response.url).pathname !== '/aufgaben/glocke') {
        window.location.href = response.url;
        return;
      }

      if (!response.ok) throw new Error(`Glocke request failed: ${response.status}`);

      liste.innerHTML = await response.text();

      // That request stamped the rows it handed out as seen, so the badge is
      // now stale by exactly those rows.
      await refreshBadge();
    } catch {
      liste.innerHTML =
        '<div class="p-4 text-center text-sm text-error">Aufgaben konnten nicht geladen werden.</div>';
      // Left false, so the next opening tries again rather than showing the
      // error until the page is reloaded.
      geladen = false;
    }
  });

  // <details> is the accessible choice here but natively closes only via its own
  // summary, so the outside-click and Escape behaviour is added the same way
  // userMenu.js adds it.
  //
  // Copied rather than shared: two menus needing the same three listeners is not
  // shared behaviour, and folding this into userMenu.js would make that file
  // about "the menus in the navbar" instead of about one menu.
  const close = () => menu.removeAttribute('open');

  document.addEventListener('click', (event) => {
    if (!menu.contains(event.target)) close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !menu.hasAttribute('open')) return;
    close();
    menu.querySelector('summary')?.focus();
  });

  // A row in the panel navigates to the record; the panel should not stay open
  // behind it.
  liste.addEventListener('click', (event) => {
    if (event.target.closest('a')) close();
  });
}
